/**
 * The Thothica mark, and the line that says who built this.
 *
 * KSIDC's system, Thothica's making. The mark belongs in the footer at a
 * maker's-mark size, not in the masthead. Nothing on an applicant screen should
 * make an entrepreneur wonder whose website they are on.
 */

import { createElement } from "react";
import type { ReactElement } from "react";
import type { Bilingual } from "@ksidc/ontology";
import logoBlack from "./thothica-logo-black.png";
import logoWhite from "./thothica-logo-white.png";

export { logoBlack, logoWhite };

export interface ThothicaMarkProps {
  /** The ground the mark sits on. Black ink on white, white ink on black. */
  on?: "white" | "black";
  height?: number;
  className?: string;
}

export function ThothicaMark({
  on = "white",
  height = 17,
  className,
}: ThothicaMarkProps): ReactElement {
  return createElement("img", {
    src: on === "black" ? logoWhite : logoBlack,
    alt: "Thothica",
    className: ["thothica-mark", className].filter(Boolean).join(" "),
    style: { height: `${height}px` },
    width: Math.round(height * 3.48),
    height,
  });
}

export const BUILT_BY: Bilingual = {
  en: "Built by Thothica for the Kerala State Industrial Development Corporation.",
  ml: "കേരള സംസ്ഥാന വ്യവസായ വികസന കോർപ്പറേഷനുവേണ്ടി തോതിക്ക നിർമ്മിച്ചത്.",
};

export const GOVERNING_LAW: Bilingual = {
  en: "Kerala Industrial Single Window Clearance Boards and Industrial Township Area Development Act, 1999, with the Rules of 2000 as amended in 2021.",
  ml: "കേരള വ്യവസായ ഏകജാലക അനുമതി ബോർഡുകളും വ്യവസായ ടൗൺഷിപ്പ് ഏരിയ വികസന നിയമം, 1999; 2021-ൽ ഭേദഗതി ചെയ്ത 2000-ലെ ചട്ടങ്ങൾ സഹിതം.",
};

/** A proof of concept, and it should say so rather than imply otherwise. */
export const STATUS_NOTE: Bilingual = {
  en: "A proof of concept. Files marked as stand-ins are not real applications.",
  ml: "ഇത് ഒരു മാതൃകാ സംവിധാനമാണ്. മാതൃകയെന്ന് അടയാളപ്പെടുത്തിയ ഫയലുകൾ യഥാർത്ഥ അപേക്ഷകളല്ല.",
};

export interface ThothicaFooterProps {
  lang: "en" | "ml";
  on?: "white" | "black";
  className?: string;
}

export function ThothicaFooter({
  lang,
  on = "white",
  className,
}: ThothicaFooterProps): ReactElement {
  return createElement(
    "footer",
    { className: ["thothica-foot", className].filter(Boolean).join(" ") },
    createElement(ThothicaMark, { on, height: 15 }),
    createElement(
      "span",
      null,
      lang === "ml" ? BUILT_BY.ml : BUILT_BY.en,
      " ",
      lang === "ml" ? STATUS_NOTE.ml : STATUS_NOTE.en,
    ),
  );
}
